import { HTMLAttributes } from 'react';
import classNames from 'classnames';
import { UncontrolledTooltip } from 'reactstrap';
import { InputRadio } from 'src/Components/Controls';
import { ICertificateInfo } from 'src/Types/Certificate';
import DateFormater from 'src/Utils/DateFormater';
import { ReactComponent as CertIconAlert } from 'src/Themes/assets/CertIconAlert.svg';
import st from './CertificatesModal.module.scss';

interface ICertificateItemProps extends HTMLAttributes<HTMLLIElement> {
	certificate: ICertificateInfo;
	currentThumbprint: string;
	onCertificateClick: (cert: ICertificateInfo) => void;
	clickHelp: () => void;
}

const CertificateItem = (props: ICertificateItemProps) => {
	const {
		certificate,
		currentThumbprint,
		onCertificateClick,
		clickHelp,
		className,
		...otherProps
	} = props;

	const renderSubject = () => {
		if (certificate.subjectCompanyName) {
			return (
				<p
					className={classNames(
						st.certificate__subjectName,
						'body-font',
					)}
				>
					<span>Компания:</span>&ensp;
					{certificate.subjectCompanyName}
				</p>
			);
		} else if (certificate.subjectEmail) {
			return (
				<p
					className={classNames(
						st.certificate__subjectName,
						'body-font',
					)}
				>
					<span>Электронная почта:</span>&ensp;
					{certificate.subjectEmail}
				</p>
			);
		}
	};

	const renderPopup = (cert: ICertificateInfo) => {
		if (!cert.isDateValid) {
			return (
				<p className={st.certificate__popupText}>
					Истек срок действия сертификата.
					<br />
					Обратитесь в{' '}
					<span className={st.link} onClick={clickHelp}>
						службу поддержки
					</span>{' '}
					для продления сертификата или выпуска нового.
				</p>
			);
		}
		if (!cert.hasPrivateKey) {
			return (
				<p className={st.certificate__popupText}>
					Закрытый ключ для данного сертификата не найден.
				</p>
			);
		}
		return (
			<p className={st.certificate__popupText}>
				Невозможно проверить сертификат в связи с отсутствием доверия к
				издателю.
			</p>
		);
	};

	// Блокируем выбор только при истёкшей дате или отсутствии закрытого ключа
	// isTrustValid - прошёл ли сертификат проверку цепочки доверия (OCSP/CRL),
	// но для подписания документов важно наличие закрытого ключа и валидной даты действия сертификата, а не некорректность цепочки доверия
	const isInvalid = !certificate.isValid;
	const hasWarning = !certificate.isValid || !certificate.isTrustValid;
	const classes = classNames(className, st.certificate, {
		[st.invalidCert]: isInvalid,
	});
	return (
		<li
			className={classes}
			onClick={() => {
				return onCertificateClick(certificate);
			}}
			{...otherProps}
		>
			<div className={st.certificate__rb}>
				<InputRadio
					name="certificate"
					disabled={isInvalid}
					value={currentThumbprint === certificate.thumbprint}
				/>
			</div>

			<div className={st.certificate__content}>
				<div className={st.certificate__text}>
					<p
						className={classNames(
							st.certificate__subjectName,
							'body-font',
						)}
					>
						{certificate.subjectName}
					</p>
					{renderSubject()}
				</div>

				<div className={st.delimiter} />

				<div className={st.certificate__text}>
					<p
						className={classNames(
							st.certificate__subjectName,
							'body-font',
						)}
					>
						<span>Издатель:</span>&ensp;
						{certificate.issuerName}
					</p>
					<p
						className={classNames(
							st.certificate__subjectName,
							'body-font',
						)}
					>
						<span>Действителен по:</span>&ensp;
						{DateFormater.toDateString(certificate.validToDate)}
					</p>
				</div>
			</div>

			<div className={st.certificate__icon}>
				{hasWarning && (
					<>
						<CertIconAlert
							width="22"
							height="20"
							id={'invalcert_' + certificate.thumbprint}
						/>
						<UncontrolledTooltip
							placement="right"
							autohide={false}
							innerClassName={classNames(
								st.certificate__popup,
								'body-small',
							)}
							popperClassName={st.certificate__hint}
							target={'invalcert_' + certificate.thumbprint}
							trigger="hover"
						>
							{renderPopup(certificate)}
						</UncontrolledTooltip>
					</>
				)}
			</div>
		</li>
	);
};

export default CertificateItem;
